import React, { useCallback, useState, useMemo } from "react";

import { css, cx } from "@emotion/css";
import { type Table as TSTable } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import type { LibreViewClinicPatient } from "@/models/LibreViewModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { SelectInput } from "@/uiKit/atoms/inputs/SelectInput.tsx";
import { SelectItem } from "@/uiKit/atoms/inputs/SelectItem.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import {
  DEFAULT_COLUMN_FILTERS,
  LAST_DATA_KEYS,
  type FilterData,
  type LastDataKey,
} from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/filterFns.ts";
import { LibreviewGroupedInclusionTBRFilter } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/LibreviewGroupedInclusionTBRFilter.tsx";
import { LibreviewGroupedInclusionTIRFilter } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/LibreviewGroupedInclusionTIRFilter.tsx";

export type LibreviewInclusionFiltersProps = {
  table: TSTable<LibreViewClinicPatient>;
  selectedCount: number;
  className?: string;
};

export const LibreviewInclusionFilters: React.FC<LibreviewInclusionFiltersProps> = ({
  table,
  selectedCount,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const filterColumn = useMemo(() => table.getColumn("filterColumn"), [table]);
  const [cachedFilters, setCachedFilters] = useState<FilterData>(filterColumn?.getFilterValue() as FilterData);

  const matching = table.getFilteredRowModel().rows.length;
  const total = table.getPreFilteredRowModel().rows.length;

  const onFilterChange = useCallback(
    (data: Partial<FilterData>) => {
      setCachedFilters(prev => ({ ...prev, ...data }));
      filterColumn?.setFilterValue((prev: FilterData) => ({ ...prev, ...data }));
    },
    [filterColumn],
  );

  return (
    <div className={cx(styles.filterBar, className)}>
      <div className={styles.filterInputs}>
        <div className={styles.filterGroup}>
          <Typography variant="body" className={styles.filterLabel}>
            {t("patientSync.libreviewTable.filters.lastData")}
          </Typography>
          <div className={styles.filterControls}>
            <SelectInput
              id="sync-grouped-inclusion-last-data-filter"
              value={cachedFilters.lastDataSince}
              onChange={value =>
                onFilterChange({ lastDataSince: value && value !== "any" ? (value as LastDataKey) : undefined })
              }
            >
              {LAST_DATA_KEYS.map(key => (
                <SelectItem key={key} value={key}>
                  {t(`patientSync.libreviewTable.filters.lastDataOptions.${key}`)}
                </SelectItem>
              ))}
            </SelectInput>
          </div>
        </div>

        <Typography variant="body" className={styles.connector}>
          {t("patientSync.libreviewTable.filters.and")}
        </Typography>

        <div className={styles.filterGroup}>
          <Typography variant="body" className={styles.filterLabel}>
            {t("patientSync.libreviewTable.filters.tir")}
          </Typography>
          <div className={styles.filterControls}>
            <LibreviewGroupedInclusionTIRFilter
              value={cachedFilters.tirMax}
              onValueChange={value => onFilterChange({ tirMax: value })}
            />
          </div>
        </div>

        <Typography variant="body" className={styles.connector}>
          {t("patientSync.libreviewTable.filters.or")}
        </Typography>

        <div className={styles.filterGroup}>
          <Typography variant="body" className={styles.filterLabel}>
            {t("patientSync.libreviewTable.filters.tbr")}
          </Typography>
          <div className={styles.filterControls}>
            <LibreviewGroupedInclusionTBRFilter
              value={cachedFilters.tbrMin}
              onValueChange={value => onFilterChange({ tbrMin: value })}
            />
          </div>
        </div>

        <Button
          variant="text"
          data-action="sync.grouped-inclusion.filter"
          startIcon={<Icons.exit className={styles.icon} />}
          onClick={() => onFilterChange(DEFAULT_COLUMN_FILTERS)}
        >
          {t("patientSync.libreviewTable.filters.reset")}
        </Button>
      </div>
      <Typography variant="body" className={styles.summary}>
        {t("patientSync.libreviewTable.filters.summary", { total, matching, selected: selectedCount })}
      </Typography>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  filterBar: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
  }),
  filterInputs: css({
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: theme.spacing(16),
    alignItems: "flex-start",
  }),
  filterGroup: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  }),
  filterControls: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  }),
  filterLabel: css({
    whiteSpace: "nowrap",
  }),
  connector: css({
    alignSelf: "center",
    paddingTop: theme.spacing(4),
    color: theme.palette.text.secondary,
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
  summary: css({
    color: theme.palette.text.secondary,
  }),
});
